import React from 'react';
import { useImpressionRef } from '@noths/polaris-client-react-hooks';
import { grid } from '@noths/polaris-client-ribbons-base';
import {
  IconBasketOutlined,
  IconChevronRight,
  IconEmail,
  IconPin,
  IconStarCircleFilled,
  Image,
} from '@noths/polaris-client-ribbons-design-system';

import * as copy from '../copy';
import * as styles from './PartnerMaxiModule.styles';

export interface PartnerMaxiModuleProps {
  location: string;
  logoSrc: string;
  name: string;
  onContactPartnerClick: () => void;
  onLogoClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  onPartnerNameClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  onStorefrontClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  onVisible?: () => void;
  onVisitShopClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  partnerLinkHref: string;
  partnerRating?: number;
  partnerReviewCount?: number;
}

export const PartnerMaxiModule = ({
  location,
  logoSrc,
  name,
  onContactPartnerClick,
  onLogoClick = () => {},
  onPartnerNameClick = () => {},
  onStorefrontClick = () => {},
  onVisible = () => {},
  onVisitShopClick = () => {},
  partnerLinkHref,
  partnerRating,
  partnerReviewCount,
}: PartnerMaxiModuleProps) => {
  const partnerModuleRef = useImpressionRef<HTMLDivElement>(() => onVisible?.());
  const partnerPLPHref = `${partnerLinkHref}/products`;
  const brandReviewsText = partnerReviewCount
    ? `(${partnerReviewCount.toLocaleString()} Brand reviews)`
    : '';

  return (
    <div css={[grid, styles.partner]} ref={partnerModuleRef}>
      {logoSrc && (
        <a
          css={styles.imageWrapper}
          href={partnerLinkHref}
          onClick={onLogoClick}
          rel="nofollow"
          tabIndex={-1}
        >
          <Image alt="partner logo" css={styles.image} loading="lazy" src={logoSrc} />
        </a>
      )}
      <div css={styles.content}>
        <h2 css={styles.heading}>
          <a href={partnerLinkHref} onClick={onPartnerNameClick} rel="nofollow">
            {name}
          </a>
        </h2>
        <ul css={styles.infoItems}>
          {location && (
            <li>
              <IconPin color="var(--brand-purple-base)" height={16} width={16} /> {location}
            </li>
          )}
          {partnerRating && (
            <li>
              <IconStarCircleFilled color="var(--brand-purple-base)" height={16} width={16} />{' '}
              <strong>{partnerRating}/5</strong> {brandReviewsText}
            </li>
          )}
          <li>
            <a href={partnerLinkHref} onClick={onStorefrontClick}>
              {copy.STOREFRONT_LINK_TEXT}
              <IconChevronRight color="var(--brand-purple-base)" height={16} width={16} />
            </a>
          </li>
        </ul>
      </div>
      <div css={styles.buttons}>
        <a css={styles.button} href={partnerPLPHref} onClick={onVisitShopClick} rel="nofollow">
          <IconBasketOutlined color={`var(--brand-purple-base)`} height={16} width={16} />
          {copy.VISIT_SHOP_CTA}
        </a>
        <button css={styles.button} onClick={() => onContactPartnerClick!()} type="button">
          <IconEmail color={`var(--brand-purple-base)`} height={16} width={16} />
          {copy.CONTACT_PARTNER_CTA}
        </button>
      </div>
    </div>
  );
};
